// Analysis orchestrator: video element -> pose frames -> events -> temporal ->
// view-specific features -> categorical findings -> scores -> QC. Wires the
// pure core to the pose engine. The UI calls this and persists the result.

import { MediaPipePoseEngine, type ModelVariant } from "../pose/poseEngine";
import type { PoseFrame } from "../pose/landmarks";
import { detectGaitEvents } from "../gait/events";
import { summarizeTemporal } from "../gait/temporal";
import { computeFrontalFeatures } from "../gait/frontal";
import { computeLateralFeatures } from "../gait/lateral";
import { buildFindings } from "../gait/bands";
import { computeScores } from "../gait/scoring";
import { calibrateFromHeight } from "../gait/calibration";
import { computeClinicalMetrics } from "../gait/clinical";
import { computeSagittalRom } from "../gait/rom";
import { detectWalkingBout } from "../gait/bout";
import { checkView, viewAdvisory } from "../gait/viewCheck";
import { runQc } from "../gait/qc";
import { legVisibility, classifyLegProximity } from "../gait/robustness";
import type { Side } from "../pose/landmarks";
import type { AnalysisResult, GaitView, QcSnapshot } from "../store/types";

export interface PipelineOutput {
  frames: PoseFrame[];
  analysis: Omit<AnalysisResult, "sessionId">;
  qc: QcSnapshot & { warnings: string[] };
  fps: number;
  durationMs: number;
}

export interface PipelineOptions {
  view: GaitView;
  /** Frame aspect ratio (width / height) — needed for isotropic spatial math. */
  aspect: number;
  /** Patient height in cm — enables spatial calibration (speed, step length). */
  heightCm?: number;
  variant?: ModelVariant;
  maxFps?: number;
  wasmPath?: string;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

export async function analyzeVideo(
  video: HTMLVideoElement,
  opts: PipelineOptions,
): Promise<PipelineOutput> {
  const engine = new MediaPipePoseEngine();
  try {
    await engine.init({ variant: opts.variant ?? "full", wasmPath: opts.wasmPath });
    const frames = await engine.processVideo(video, {
      maxFps: opts.maxFps ?? 60,
      onProgress: opts.onProgress,
      signal: opts.signal,
    });

    // Analyse only the steady-state walking bout (drop start/stop/turn frames).
    // The full `frames` are still returned/stored for video overlay alignment.
    const bout = detectWalkingBout(frames);
    const aframes = frames.slice(bout.startIdx, bout.endIdx + 1);

    // Advisory: does the recording match the selected view?
    const view = checkView(frames);
    const advisory = viewAdvisory(opts.view, view);

    const eventResult = detectGaitEvents(aframes, { view: opts.view });
    const temporal = summarizeTemporal(eventResult);

    const frontal =
      opts.view === "frontal"
        ? computeFrontalFeatures(aframes, eventResult, opts.aspect)
        : undefined;
    const lateral =
      opts.view === "lateral"
        ? computeLateralFeatures(aframes, eventResult, opts.aspect)
        : undefined;

    // Spatial calibration + ROM are meaningful for the lateral (sagittal) view.
    const calibration =
      opts.view === "lateral"
        ? calibrateFromHeight(aframes, opts.aspect, opts.heightCm ?? 0)
        : null;
    const clinical = computeClinicalMetrics(aframes, eventResult, {
      aspect: opts.aspect,
      calibration,
    });
    const rom =
      opts.view === "lateral"
        ? computeSagittalRom(aframes, eventResult, opts.aspect)
        : undefined;

    const cycleCount = eventResult.cycles.length;

    // Robustness (Stage 2): per-side capture quality + near/far-leg split, so
    // side findings carry honest confidence and the occluded far leg isn't
    // trusted equally. legProximity is only meaningful for a lateral clip.
    const sideVisibility: Record<Side, number> = {
      L: legVisibility(aframes, "L"),
      R: legVisibility(aframes, "R"),
    };
    const sideCycleCount: Record<Side, number> = {
      L: eventResult.cycles.filter((c) => c.side === "L").length,
      R: eventResult.cycles.filter((c) => c.side === "R").length,
    };
    const legProximity =
      opts.view === "lateral" ? classifyLegProximity(aframes) : undefined;

    const findings = buildFindings({
      view: opts.view,
      temporal,
      frontal,
      lateral,
      meanVisibility: eventResult.meanVisibility,
      cycleCount,
      sideVisibility,
      sideCycleCount,
    });
    const scores = computeScores({
      view: opts.view,
      temporal,
      frontal,
      lateral,
      meanVisibility: eventResult.meanVisibility,
      cycleCount,
    });

    const durationMs = video.duration * 1000;
    const fps = frames.length > 1 ? frames.length / (durationMs / 1000) : 0;
    const qcReport = runQc(aframes, cycleCount, fps);
    if (advisory) qcReport.warnings.unshift(advisory);

    return {
      frames,
      analysis: {
        events: eventResult.events,
        cycles: eventResult.cycles,
        temporal,
        direction: eventResult.direction,
        findings,
        scores,
        frontal,
        lateral,
        clinical,
        rom,
        boutMs: { startMs: bout.startMs, endMs: bout.endMs },
        viewAdvisory: advisory,
        legProximity,
      },
      qc: {
        passed: qcReport.passed,
        fullBodyRatio: qcReport.fullBodyRatio,
        meanConfidence: qcReport.meanConfidence,
        fps: qcReport.fps,
        detectedCycles: qcReport.detectedCycles,
        warnings: qcReport.warnings,
      },
      fps,
      durationMs,
    };
  } finally {
    engine.close();
  }
}
